import { Injectable } from '@angular/core'
import {
  CreateEventRequest,
  DeleteEventResponse,
  HearingSectionEventResponse,
  ListPublicHearingSectionEventsRequest,
  MutateHearingSectionEventResponse,
  UpdateEventRequest,
} from '@ftr/annotations-contracts/v2'
import { UnauthorizedApiError } from '@ftr/api-shared'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, RemoteData } from '@ftr/foundation'
import { featureFlag } from '@ftr/ui-feature-flags'
import { of } from 'rxjs'
import { AnnotationsService } from '../base'

// TODO: Pull this from the definitions on the API once its coming down on the contracts/endpoints
export const HearingSectionEventType = {
  BLANK: 'BLANK/V1',
  CASE_OUTCOME: 'CASE_OUTCOME/V1',
  APPEARANCE: 'APPEARANCE/V1',
} as const

export interface ListHearingSectionEventsParams {
  courtSystemId: Uuid
  recordingId: Uuid
  hearingSectionId?: Uuid
  eventTypes?: string[]
}

@Injectable({
  providedIn: 'root',
})
export class HearingSectionEventsService extends AnnotationsService {
  private readonly hasAnnotationsEvents = featureFlag('feature-annotations-events')
  private readonly hasAnnotationsAppearances = featureFlag('feature-annotations-appearance')

  listHearingSectionEvents(params: ListHearingSectionEventsParams): ApiResult<HearingSectionEventResponse[]> {
    if (!this.hasAnnotationsEvents() && !this.hasAnnotationsAppearances()) {
      return of(RemoteData.success([]))
    }

    return this.performApiRequestV2({
      courtSystemId: params.courtSystemId,
      requestFn: client =>
        client.hearingSectionEvents.listEvents({
          params: { courtSystemId: params.courtSystemId },
          query: {
            recordingId: params.recordingId,
            hearingSectionId: params.hearingSectionId,
            ...(params.eventTypes?.length ? { eventTypes: params.eventTypes.join(',') } : {}),
          },
        }),
      responseMapFn: result => {
        if (result.status === 401 || result.status === 403) {
          return RemoteData.failure(new UnauthorizedApiError())
        }

        if (result.status === 200) {
          return RemoteData.success(result.body.items)
        }

        return RemoteData.failure(new Error('Failed to list hearing section events'))
      },
    })
  }

  listPublicHearingSectionEvents(
    params: Omit<ListPublicHearingSectionEventsRequest, 'requestType'>,
  ): ApiResult<HearingSectionEventResponse[]> {
    if (!this.hasAnnotationsEvents() && !this.hasAnnotationsAppearances()) {
      return of(RemoteData.success([]))
    }
    const { courtSystemId, ...restParams } = params

    return this.performApiRequestV2Public({
      courtSystemId,
      requestFn: client =>
        client.hearingSectionEvents.listPublicEvents({
          query: {
            courtSystemId,
            requestType: 'AUDIO_SEGMENT',
            audioSegmentId: restParams.audioSegmentId,
          },
        }),
      responseMapFn: result => {
        if (result.status === 401 || result.status === 403) {
          return RemoteData.failure(new UnauthorizedApiError())
        }

        if (result.status === 200) {
          return RemoteData.success(result.body.items)
        }

        return RemoteData.failure(new Error('Failed to list hearing section events'))
      },
    })
  }

  updateHearingSectionEvent(
    courtSystemId: Uuid,
    eventId: Uuid,
    body: Pick<UpdateEventRequest, 'startTime' | 'endTime' | 'resources' | 'attributes'>,
  ): ApiResult<MutateHearingSectionEventResponse | null> {
    return this.performApiRequestV2({
      courtSystemId,
      requestFn: client =>
        client.hearingSectionEvents.updateEvent({
          body,
          params: { courtSystemId, eventId },
        }),
      responseMapFn: result => {
        if (result.status === 401 || result.status === 403) {
          return RemoteData.failure(new UnauthorizedApiError())
        }

        // Surface the whole envelope ({ item, metadata.effects }) so callers can toast the effects.
        if (result.status === 200) {
          return RemoteData.success(result.body)
        }

        return RemoteData.failure(new Error('Failed to update event'))
      },
    })
  }

  deleteHearingSectionEvent(courtSystemId: Uuid, eventId: Uuid): ApiResult<DeleteEventResponse | null> {
    return this.runIfFeatureFlagEnabled(
      'feature-annotations-appearance',
      ApiResult.success(null),
      this.performApiRequestV2({
        courtSystemId,
        requestFn: client =>
          client.hearingSectionEvents.deleteEvent({
            params: { courtSystemId, eventId },
          }),
        responseMapFn: result => {
          if (result.status === 401 || result.status === 403) {
            return RemoteData.failure(new UnauthorizedApiError())
          }

          // 200 carries { metadata.effects } describing the removal; surface it for toasting.
          if (result.status === 200) {
            return RemoteData.success(result.body)
          }

          // Tolerate the legacy 204 No Content (no body, no effects to surface).
          const status: number = result.status
          if (status === 204) {
            return RemoteData.success(null)
          }

          return RemoteData.failure(new Error('Failed to delete appearance'))
        },
      }),
    )
  }

  createHearingSectionEvent(
    courtSystemId: Uuid,
    body: CreateEventRequest,
  ): ApiResult<MutateHearingSectionEventResponse | null> {
    return this.runIfFeatureFlagEnabled(
      'feature-annotations-appearance',
      ApiResult.success(null),
      this.performApiRequestV2({
        courtSystemId,
        requestFn: client =>
          client.hearingSectionEvents.createEvent({
            body,
            params: { courtSystemId },
          }),
        responseMapFn: result => {
          if (result.status === 401 || result.status === 403) {
            return RemoteData.failure(new UnauthorizedApiError())
          }

          // Surface the whole envelope ({ item, metadata.effects }) so callers can toast the effects.
          if (result.status === 201) {
            return RemoteData.success(result.body)
          }

          return RemoteData.failure(new Error('Failed to create event'))
        },
      }),
    )
  }
}
